import React from 'react';

import { LoadingPanel } from '../common/LoadingPanel';
import { FocusPanel } from '../focus-panel/FocusPanel.view';
import { PollToConnectView } from './PollToConnect.view';
import { HibernatedMessage } from './HibernatedMessage.view';
import { NotNodeMessage } from './NotNodeMessage.view';
import { NetworkProblemMessage } from './NetworkProblemMessage.view';
import { IdMismatchMessage } from './IdMismatchMessage.view';
import { UnlicensedMessage } from './UnlicensedMessage.view';
import { AccessDeniedMessage } from './AccessDeniedMessage.view';
import { ProvisioningFailed } from './ProvisioningFailed.view';
import { NotConnectedMessage } from './NotConnected.view';
import { UnknownReasonMessage } from './UnknownReasonMessage.view';
import { ProvisioningMessage } from './ProvisioningMessage.view';
import { NoDefaultConnectionMessage } from './NoDefaultConnectionMessage.view';

import type { ConnectSubscriptionStatus } from 'src/types/subscription.types';

import './ConnectMessages.scss';

const getMessage = (args: {
  connectStatus: ConnectSubscriptionStatus;
  lastUrl?: string;
  subId: string;
}): React.ReactNode => {
  const { connectStatus, lastUrl = '', subId = '' } = args;

  const ConnectMessagesMapTable: Record<ConnectSubscriptionStatus, JSX.Element> = {
    access_denied: <AccessDeniedMessage />,
    checking_connection: <LoadingPanel loadingMessage="Checking connection status..." />,
    completed: <LoadingPanel loadingMessage="Connecting..." />,
    connection_failed: <NotConnectedMessage />,
    destroyed: <NotNodeMessage lastUrl={lastUrl} />,
    failed: <ProvisioningFailed subId={subId} />,
    hibernated: <HibernatedMessage />,
    id_mismatch: <IdMismatchMessage subId={subId} lastUrl={lastUrl} />,
    network_problem: <NetworkProblemMessage lastUrl={lastUrl} />,
    no_default_connection: <NoDefaultConnectionMessage />,
    not_a_node: <NotNodeMessage lastUrl={lastUrl} />,
    not_found: <NotNodeMessage lastUrl={lastUrl} />,
    not_json: <NotNodeMessage lastUrl={lastUrl} />,
    okay: <PollToConnectView />,
    pending: <ProvisioningMessage />,
    poll_until_connect: <PollToConnectView />,
    provisioning: <ProvisioningMessage />,
    trying_to_connect: <LoadingPanel loadingMessage="Connecting..." />,
    unlicensed: <UnlicensedMessage lastUrl={lastUrl} subId={subId} />,
  };

  return (
    ConnectMessagesMapTable[connectStatus] ?? <UnknownReasonMessage connectStatus={connectStatus} />
  );
};

export const ConnectMessages: React.FC<{
  connectStatus: ConnectSubscriptionStatus;
  lastUrl?: string;
  subId?: string;
}> = ({ connectStatus, lastUrl = '', subId = '' }) => {
  const component = getMessage({ connectStatus, subId, lastUrl });

  if (['trying_to_connect', 'checking_connection'].includes(connectStatus)) {
    return component;
  }

  return <FocusPanel>{component}</FocusPanel>;
};
